import { useEffect, useState } from "react";
import { collection, getCountFromServer, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";

export interface AdminStats {
  // Deal Stats
  activeDeals: number;
  soldDeals: number;
  totalDealValue: string | undefined;
  avgTimeOnMarketDays: number | undefined;
  
  // Activity Stats (30 days)
  ndas30d: number;
  offers30d: number;
  favorites30d: number;
  submissions30d: number;
  
  // User Stats
  totalUsers: number;
  totalSellers: number;
  totalBrokers: number;
  totalBuyers: number;
  totalConsultants: number;
  totalLenders: number;
  
  // Staff & Productivity
  staffDealsInProgress: number;
  cimReadyForReview: number;
  partnerAssignmentsOpen: number;
  
  // Financial Stats
  monthlyRevenue: number;
  pendingPayments: number;
  
  // Conversion Rates
  submissionToListedRate: number;
  ndaToOfferRate: number;

  // AI Documents Stats
  totalCimsGenerated: number;
  totalMarketStudies: number;
  totalStrategicPlans: number;
  totalCompetitionReports: number;
  aiDocsGenerated30d: number;
}

export function useAdminStats() {
  const [stats, setStats] = useState<AdminStats>({
    activeDeals: 0,
    soldDeals: 0,
    totalDealValue: undefined,
    avgTimeOnMarketDays: undefined,
    ndas30d: 0,
    offers30d: 0,
    favorites30d: 0,
    submissions30d: 0,
    totalUsers: 0,
    totalSellers: 0,
    totalBrokers: 0,
    totalBuyers: 0,
    totalConsultants: 0,
    totalLenders: 0,
    staffDealsInProgress: 0,
    cimReadyForReview: 0,
    partnerAssignmentsOpen: 0,
    monthlyRevenue: 0,
    pendingPayments: 0,
    submissionToListedRate: 0,
    ndaToOfferRate: 0,
    totalCimsGenerated: 0,
    totalMarketStudies: 0,
    totalStrategicPlans: 0,
    totalCompetitionReports: 0,
    aiDocsGenerated30d: 0,
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const now = new Date();
        const d30 = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        // Helper pour sécuriser le comptage
        const getCount = async (q: ReturnType<typeof query>) => {
          try {
            const snapshot = await getCountFromServer(q);
            return snapshot.data().count;
          } catch (e) {
            console.error("Failing query:", e);
            return 0;
          }
        };

        // Deals queries — statuts élargis (draft, pending, cim_generation ajoutés)
        const activeQ = query(
          collection(db, "deals"),
          where("status", "in", ["Listed", "Under NDA", "Offers", "Under LOI", "draft", "pending", "cim_generation", "review_in_progress"])
        );
        const soldQ = query(collection(db, "deals"), where("status", "==", "Sold"));

        // Staff productivity queries — cim_generation ajouté
        const staffDealsQ = query(
          collection(db, "deals"),
          where("status", "in", ["review_in_progress", "evaluation_in_progress", "cim_generation"])
        );
        const cimReviewQ = query(
          collection(db, "deals"),
          where("status", "==", "cim_ready_for_internal_review")
        );

        // Activity queries (30 days)
        const ndasQ = query(collection(db, "ndas"), where("signedAt", ">=", d30));
        const offersQ = query(collection(db, "offers"), where("createdAt", ">=", d30));
        const favsQ = query(collection(db, "favorites"), where("createdAt", ">=", d30));
        const subsQ = query(collection(db, "submissions"), where("createdAt", ">=", d30));

        // User counts by role
        const usersQ = query(collection(db, "users"));
        const sellersQ = query(collection(db, "users"), where("role", "==", "seller"));
        const brokersQ = query(collection(db, "users"), where("role", "==", "broker"));
        const buyersQ = query(collection(db, "users"), where("role", "==", "buyer"));
        const consultantsQ = query(collection(db, "users"), where("role", "==", "consultant"));
        const lendersQ = query(collection(db, "users"), where("role", "==", "lender"));

        // Partner assignments
        const partnerAssignQ = query(
          collection(db, "partnerAssignments"),
          where("status", "==", "open")
        );

        // AI Documents queries
        const cimsQ = query(collection(db, "cimData"));
        const marketStudiesQ = query(collection(db, "marketStudies"));
        const strategicPlansQ = query(collection(db, "strategicPlans"));
        const competitionReportsQ = query(collection(db, "competitionReports"));

        // Execute all queries in parallel
        const [
          active, sold, staff, review,
          ndas, offers, favs, subs,
          users, sellers, brokers, buyers, consultants, lenders,
          partnerAssign,
          cims, marketStudies, strategicPlans, competitionReports,
        ] = await Promise.all([
          getCount(activeQ), getCount(soldQ), getCount(staffDealsQ), getCount(cimReviewQ),
          getCount(ndasQ), getCount(offersQ), getCount(favsQ), getCount(subsQ),
          getCount(usersQ), getCount(sellersQ), getCount(brokersQ), getCount(buyersQ), getCount(consultantsQ), getCount(lendersQ),
          getCount(partnerAssignQ),
          getCount(cimsQ), getCount(marketStudiesQ), getCount(strategicPlansQ), getCount(competitionReportsQ),
        ]);

        // Calculate conversion rates
        const submissionToListedRate = subs > 0 ? (active / subs) * 100 : 0;
        const ndaToOfferRate = ndas > 0 ? (offers / ndas) * 100 : 0;

        const aiDocsTotal = cims + marketStudies + strategicPlans + competitionReports;

        setStats({
          activeDeals: active,
          soldDeals: sold,
          totalDealValue: undefined,
          avgTimeOnMarketDays: undefined,
          ndas30d: ndas,
          offers30d: offers,
          favorites30d: favs,
          submissions30d: subs,
          totalUsers: users,
          totalSellers: sellers,
          totalBrokers: brokers,
          totalBuyers: buyers,
          totalConsultants: consultants,
          totalLenders: lenders,
          staffDealsInProgress: staff,
          cimReadyForReview: review,
          partnerAssignmentsOpen: partnerAssign,
          monthlyRevenue: 0,
          pendingPayments: 0,
          submissionToListedRate,
          ndaToOfferRate,
          totalCimsGenerated: cims,
          totalMarketStudies: marketStudies,
          totalStrategicPlans: strategicPlans,
          totalCompetitionReports: competitionReports,
          aiDocsGenerated30d: aiDocsTotal,
        });
      } catch (error) {
        console.error("Error fetching admin stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return { stats, loading };
}
